import {DataFetcher} from '@aboutyou/data-fetcher';
import {ShopTheLookSection as ShopTheLookSectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  EntityList,
  NumberBadge,
  TabContentItem,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled from 'styled-components';
import {InteractiveProductTile} from '../../../../features/ProductTile/InteractiveProductTile';
import {ProductTileProps} from '../../../../features/ProductTile/ProductTile';
import {ProductTileConfig} from '../../../../features/Recommendations/ProductList';
import {SizeSelectionOpenedStateKey} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {MiniArticleDetail} from '../MiniArticleDetail/MiniArticleDetail';
import {useShopTheLookSection} from '../useGetProductDetailsBulkSection';
import {LoadingTab} from './LoadingTab';

export const ShopTheLookTab: FC<{
  onAddToBasketCtaClick?: ({
    productId,
    couponPromotionId,
  }: {
    productId: number;
    couponPromotionId?: string;
  }) => void;
  onAddToBasketSuccess: VoidFunction;
  productTileConfig?: ProductTileConfig;
}> = props => {
  return (
    <DataFetcher
      request={useShopTheLookSection()}
      Error={() => null}
      viewProps={props}
      View={ShopTheLookTabView}
      Loading={Loading}
    />
  );
};
export const SHOP_THE_LOOK_TAB_NAME = 'SHOP_THE_LOOK';

const ShopTheLookTabView: React.FC<
  ShopTheLookSectionType & {
    onAddToBasketCtaClick?: ({
      productId,
      couponPromotionId,
    }: {
      productId: number;
      couponPromotionId?: string;
    }) => void;
    onAddToBasketSuccess: VoidFunction;
    productTileConfig?: ProductTileConfig;
  }
> = ({
  shopTheLook,
  onAddToBasketCtaClick,
  onAddToBasketSuccess,
  productTileConfig,
}) => {
  const {t} = useTranslation();

  const Tile: React.FC<Omit<ProductTileProps, 'ProductTileComponent'>> =
    InteractiveProductTile;

  if (!shopTheLook?.shopTheLook) {
    return null;
  }
  const content = (
    <>
      {shopTheLook?.shopTheLook?.$case === 'singleProduct' ? (
        <MiniArticleDetail
          imagesSection={shopTheLook.shopTheLook.singleProduct.imagesSection}
          priceAndBrandSection={
            shopTheLook.shopTheLook.singleProduct.priceAndBrandSection
          }
          productSelectionSection={
            shopTheLook.shopTheLook.singleProduct.productSelectionSection
          }
          sizesSection={shopTheLook.shopTheLook.singleProduct.sizesSection}
          onAddToBasketSuccess={onAddToBasketSuccess}
          SizeSelectionStateManagerKey={
            SizeSelectionOpenedStateKey.SHOP_THE_LOOK_SIZE_SELECTION
          }
        />
      ) : (
        <EntityList
          type="slider"
          hideSideButtons
          constrained
          slideCount={2}
          slideCountSmall={2}
          slideCountMedium={3}
          slideCountLarge={4}
          uniqueKey="entityList_shopTheLook"
        >
          {shopTheLook?.shopTheLook?.productTiles.productTiles.map(
            (tile, index) => (
              <Tile
                streamPosition={index}
                tile={tile}
                key={tile.productId}
                onClickAddCta={() =>
                  onAddToBasketCtaClick?.({
                    productId: tile.productId,
                    couponPromotionId: tile.couponPromotionId,
                  })
                }
                addCtaLabel={t('Add to basket')}
                trackingData={{
                  recoType: 'shopTheLookReco',
                  elements: [
                    {
                      id: tile.productId,
                      type: 'product',
                    },
                  ],
                }}
                gridColumns={productTileConfig?.gridColumns}
                onColorClick={productTileConfig?.onColorClick}
              />
            ),
          )}
        </EntityList>
      )}
    </>
  );
  const headline = t('Shop the look');
  const productCount =
    shopTheLook?.shopTheLook?.$case === 'productTiles'
      ? shopTheLook.shopTheLook.productTiles.productTiles.length
      : 1;

  return (
    <TabContentItem
      name={SHOP_THE_LOOK_TAB_NAME}
      label={state => (
        <>
          {headline}
          <Count
            active
            activeStyles={
              state.isActive
                ? {
                    activeBg: COLORS.Black,
                    activeColor: COLORS.White,
                  }
                : {
                    activeBg: COLORS.Grey4,
                    activeColor: COLORS.Grey1,
                  }
            }
            count={productCount}
            size="small"
          />
        </>
      )}
      content={content}
    />
  );
};

const Loading: FC = () => {
  return <LoadingTab name={SHOP_THE_LOOK_TAB_NAME} />;
};

const Count = styled(NumberBadge)`
  margin-left: 8px;
  min-width: unset;
  vertical-align: bottom;
`;
